import React, {useCallback, useEffect, useState} from 'react';
import logsService from "../../../services/logs.service";
import Loading from "../../common/Loading";
import {Badge, Button, Card, Collapse, Pagination} from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const LogsDashboard = () => {
    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [pagination, setPagination] = useState({
        page: 0,
        size: 10,
        totalElements: 0,
        totalPages: 0
    });
    const [copiedId, setCopiedId] = useState(null);
    const [expandedRows, setExpandedRows] = useState([]);

    const fetchLogs = useCallback(async () => {
        let isMounted = true;
        const controller = new AbortController();

        try {
            setIsLoading(true);
            setError(null);
            setExpandedRows([]);

            const dateParam = selectedDate ? selectedDate.toISOString().split('T')[0] : '';

            const res = await logsService.getLogs(
                pagination.page,
                pagination.size,
                dateParam,
                {signal: controller.signal}
            );

            if (isMounted) {
                setLogs(res.data.content || []);
                setPagination(prev => ({
                    ...prev,
                    totalElements: res.data.totalElements || 0,
                    totalPages: res.data.totalPages || 0
                }));
            }
        } catch (err) {
            if (err.name !== 'AbortError' && isMounted) {
                setError('Error: No logs found');
            }
        } finally {
            if (isMounted) setIsLoading(false);
        }

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [pagination.page, pagination.size, selectedDate]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const handleDateChange = (date) => {
        setSelectedDate(date);
        setPagination(prev => ({...prev, page: 0})); // Reset to first page when date changes
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({...prev, page: newPage}));
    };

    const handleSizeChange = (e) => {
        const newSize = Number(e.target.value);
        setPagination(prev => ({...prev, size: newSize, page: 0}));
    };

    const clearDateFilter = () => {
        setSelectedDate(null);
        setPagination(prev => ({...prev, page: 0}));
    };

    const copyToClipboard = (id) => {
        navigator.clipboard.writeText(id);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const formatDuration = (ms) => {
        return `${ms}ms`;
    };

    const toggleRow = (id) => {
        setExpandedRows(prev =>
            prev.includes(id)
                ? prev.filter(rowId => rowId !== id)
                : [...prev, id]
        );
    };

    const formatJson = (obj) => {
        if (!obj) return 'N/A';
        try {
            return JSON.stringify(obj, null, 2);
        } catch (e) {
            return 'Invalid JSON';
        }
    };

    // Function to generate pagination items with ellipses
    const getPaginationItems = () => {
        const items = [];
        const currentPage = pagination.page;
        const totalPages = pagination.totalPages;
        const maxVisible = 5; // Maximum number of page buttons to show

        // Always show first page
        items.push(
            <Pagination.Item
                key={1}
                active={1 === currentPage + 1}
                onClick={() => handlePageChange(0)}
                disabled={isLoading}
            >
                {1}
            </Pagination.Item>
        );

        // Calculate start and end of visible page range
        let startPage = Math.max(2, currentPage - 1);
        let endPage = Math.min(totalPages - 1, currentPage + 3);

        // Adjust if we're near the beginning
        if (currentPage < 3) {
            endPage = Math.min(totalPages - 1, maxVisible);
        }

        // Adjust if we're near the end
        if (currentPage > totalPages - 4) {
            startPage = Math.max(2, totalPages - maxVisible + 1);
        }

        // Add ellipsis after first page if needed
        if (startPage > 2) {
            items.push(<Pagination.Ellipsis key="start-ellipsis" disabled/>);
        }

        // Add page numbers in the calculated range
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage + 1}
                    onClick={() => handlePageChange(number - 1)}
                    disabled={isLoading}
                >
                    {number}
                </Pagination.Item>
            );
        }

        // Add ellipsis before last page if needed
        if (endPage < totalPages - 1) {
            items.push(<Pagination.Ellipsis key="end-ellipsis" disabled/>);
        }

        // Always show last page if there is more than one page
        if (totalPages > 1) {
            items.push(
                <Pagination.Item
                    key={totalPages}
                    active={totalPages === currentPage + 1}
                    onClick={() => handlePageChange(totalPages - 1)}
                    disabled={isLoading}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    return (
        <div className="container-fluid mt-4">
            <Card className="card-padding p-3">
                <Card.Body>
                    <div className="d-flex justify-content-between mb-3">
                        <div className="d-flex align-items-center">
                            <span className="me-2">Show</span>
                            <select
                                className="form-select form-select-sm"
                                style={{width: '80px'}}
                                value={pagination.size}
                                onChange={handleSizeChange}
                                disabled={isLoading}
                            >
                                {[10, 20, 50].map(size => (
                                    <option key={size} value={size}>{size}</option>
                                ))}
                            </select>
                            <span className="ms-2">entries</span>
                        </div>

                        <div className="d-flex align-items-center">
                            <div className="me-3 position-relative">
                                <DatePicker
                                    selected={selectedDate}
                                    onChange={handleDateChange}
                                    dateFormat="yyyy-MM-dd"
                                    className="form-control form-control-sm pe-4"
                                    placeholderText="Select date"
                                    maxDate={new Date()}
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                />
                                {selectedDate && (
                                    <button
                                        type="button"
                                        className="btn btn-danger btn-sm position-absolute end-0 top-50 translate-middle-y me-1"
                                        title="Clear date filter"
                                        style={{
                                            transform: 'translateY(-50%)',
                                            lineHeight: 1,
                                            padding: '0.25rem'
                                        }}
                                        onClick={clearDateFilter}
                                    >
                                        <i className="bi bi-x"></i>
                                    </button>
                                )}
                            </div>
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={fetchLogs}
                                disabled={isLoading}
                            >
                                <i className={isLoading ? "bi bi-arrow-clockwise spin" : "bi bi-arrow-clockwise"}></i>
                            </Button>
                        </div>
                    </div>

                    {isLoading ? (
                        <Loading/>
                    ) : (
                        <>
                            {error ? (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            ) : logs.length === 0 ? (
                                <tbody>
                                <tr>
                                    <td colSpan="8" className="text-center py-4">
                                        No scheduled logs found
                                    </td>
                                </tr>
                                </tbody>
                            ) : (
                                <>
                                    <div className="table-responsive small">
                                        <table className="table table-bordered table-hover align-middle">
                                            <thead className="table-header">
                                            <tr>
                                                <th className="col-6"><i className="bi bi-list-task"></i> Job Name</th>
                                                <th className="col-2"><i className="bi bi-calendar-date"></i> Execution
                                                    Date
                                                </th>
                                                <th className="col-1"><i className="bi bi-clock-history"></i> Status
                                                </th>
                                                <th className="col-2"><i className="bi bi-stopwatch"></i> Time Taken
                                                </th>
                                                <th className="col-1"><i className="bi bi-gear"></i> Actions</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {logs.length > 0 ? (
                                                logs.map(log => (
                                                    <React.Fragment key={log.id}>
                                                        <tr>
                                                            <td>
                                                                <div>{log.name}</div>
                                                            </td>
                                                            <td>
                                                                {log.executionDate}
                                                            </td>
                                                            <td>
                                                                <Badge
                                                                    bg={log.status === 'EXECUTED' ? 'success' :
                                                                        log.status === 'FAILED' ? 'danger' : 'warning'}
                                                                    className="text-uppercase"
                                                                >
                                                                    {log.status}
                                                                </Badge>
                                                            </td>
                                                            <td>{formatDuration(log.timeTaken)}</td>
                                                            <td>

                                                                <Button
                                                                    variant={expandedRows.includes(log.id) ? 'outline-danger' : 'outline-primary'}
                                                                    size="sm"
                                                                    onClick={() => toggleRow(log.id)}
                                                                    title={expandedRows.includes(log.id) ? 'Collapse' : 'Expand'}
                                                                >
                                                                    <i className={`bi bi-chevron-${expandedRows.includes(log.id) ? 'up' : 'down'}`}></i>
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td colSpan="5" className="p-0 border-top-0">
                                                                <Collapse in={expandedRows.includes(log.id)}>
                                                                    <div className="p-3 bg-light">
                                                                        <div className="row mb-2">
                                                                            <div className="col-md-12">
                                                                                <span><strong>ID:</strong> <span
                                                                                    className="small font-monospace"> {log.id}&nbsp;</span></span>
                                                                                <button
                                                                                    onClick={() => copyToClipboard(log.id)}
                                                                                    title="Copy ID"
                                                                                    className="btn btn-outline-primary btn-sm me-2"
                                                                                >
                                                                                    {copiedId === log.id ? (
                                                                                        <i className="bi bi-check"></i>
                                                                                    ) : (
                                                                                        <i className="bi bi-copy"></i>
                                                                                    )}
                                                                                </button>
                                                                            </div>
                                                                            <div className="p-1"></div>
                                                                            <div className="col-md-6">
                                                                                <strong>Start
                                                                                    Time:</strong> {log.startTime}
                                                                            </div>
                                                                            <div className="col-md-6">
                                                                                <strong>End
                                                                                    Time:</strong> {log.endTime}
                                                                            </div>
                                                                            <div className="p-1"></div>
                                                                            <div className="col-md-4">
                                                                                <strong>Email
                                                                                    Sent:</strong> {log.isEmailResult ? 'Yes' : 'No'}
                                                                            </div>
                                                                        </div>
                                                                        <div className="row mb-2">
                                                                            <div className="col-md-4">
                                                                                <strong>Email
                                                                                    Status:</strong> {log.emailStatus || 'N/A'}
                                                                            </div>
                                                                        </div>
                                                                        <div className="mt-2">
                                                                            <strong>Result:</strong>
                                                                            <code>
                                                                    <pre className="bg-white p-2 mt-2 rounded"
                                                                         style={{maxHeight: '200px', overflow: 'auto'}}>
                                                                            {formatJson(log.result)}
                                                                        </pre>
                                                                            </code>
                                                                        </div>
                                                                    </div>
                                                                </Collapse>
                                                            </td>
                                                        </tr>
                                                    </React.Fragment>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="6" className="text-center py-4">
                                                        No scheduled logs found
                                                    </td>
                                                </tr>
                                            )}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div>
                                            Showing {logs.length > 0 ? pagination.page * pagination.size + 1 : 0} to{' '}
                                            {Math.min((pagination.page + 1) * pagination.size, pagination.totalElements)} of{' '}
                                            {pagination.totalElements} entries
                                        </div>

                                        {pagination.totalPages > 1 && (
                                            <Pagination>
                                                <Pagination.First
                                                    onClick={() => handlePageChange(0)}
                                                    disabled={pagination.page === 0 || isLoading}
                                                />
                                                <Pagination.Prev
                                                    onClick={() => handlePageChange(pagination.page - 1)}
                                                    disabled={pagination.page === 0 || isLoading}
                                                />

                                                {getPaginationItems()}

                                                <Pagination.Next
                                                    onClick={() => handlePageChange(pagination.page + 1)}
                                                    disabled={pagination.page === pagination.totalPages - 1 || isLoading}
                                                />
                                                <Pagination.Last
                                                    onClick={() => handlePageChange(pagination.totalPages - 1)}
                                                    disabled={pagination.page === pagination.totalPages - 1 || isLoading}
                                                />
                                            </Pagination>
                                        )}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default LogsDashboard;